import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { jobsApi } from '../services/api';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Play, RefreshCw, AlertCircle, FileText,
  Database, FolderInput, FolderOutput, Server, Building2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';

function StatusBadge({ status }) {
  const map = {
    success: 'badge-success', failed: 'badge-failed',
    partial: 'badge-partial', running: 'badge-running', pending: 'badge-pending',
  };
  return (
    <span className={`badge ${map[status] || 'badge-pending'}`}>
      <span className="badge-dot" />
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
}

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default function Dashboard() {
  const [stats, setStats]     = useState(null);
  const [running, setRunning] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchStats = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await jobsApi.getDashboard();
      setStats(data);
    } catch (err) {
      if (!silent) toast.error('Failed to load dashboard');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(() => fetchStats(true), 15000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const { data } = await jobsApi.trigger();
      toast.success('Sync job started!');
      navigate(`/jobs/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start job');
    } finally {
      setRunning(false);
    }
  };

  if (!stats) return <div className="loading-screen"><div className="spinner" /></div>;

  const lastSync      = stats.last_sync ? new Date(stats.last_sync) : null;
  const isConfigured  = stats.connection_configured;
  const isSFTP        = stats.connection_type === 'sftp';

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            {(user?.tenant?.name || user?.tenant_id) && (
              <span style={{ marginRight: 8, padding: '2px 8px', background: 'var(--navy)', borderRadius: 4, fontSize: 12, color: 'var(--blue)', border: '1px solid var(--navy-border)' }}>
                {user?.tenant?.name || 'Tenant'}
              </span>
            )}
            {lastSync
              ? `Last sync ${formatDistanceToNow(lastSync, { addSuffix: true })}`
              : 'No syncs yet — run your first job below'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => fetchStats()} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} /> Refresh
          </button>
          <button
            className={`btn btn-primary ${running ? 'btn-loading' : ''}`}
            onClick={handleRunNow}
            disabled={running || !isConfigured}
            title={!isConfigured ? 'Configure a connection in Connection Settings first' : ''}
          >
            <Play size={15} /> {running ? 'Starting…' : 'Run Now'}
          </button>
        </div>
      </div>

      {/* No connection warning */}
      {!isConfigured && (
        <div className="alert-box alert-error" style={{ marginBottom: 20 }}>
          <AlertCircle size={18} />
          <div>
            No connection configured.{' '}
            <Link to="/connection" style={{ color: 'inherit', fontWeight: 600 }}>
              Set up Connection Settings →
            </Link>
          </div>
        </div>
      )}

      {/* Stats grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-accent" style={{ background: 'var(--blue)' }} />
          <div className="stat-label">Last Status</div>
          <div style={{ marginTop: 6 }}>
            {stats.last_status
              ? <StatusBadge status={stats.last_status} />
              : <span style={{ color: 'var(--text-dim)' }}>—</span>}
          </div>
          <div className="stat-sub">{lastSync ? format(lastSync, 'MMM d, h:mm a') : 'Never run'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-accent" style={{ background: 'var(--green)' }} />
          <div className="stat-label">Successful (24h)</div>
          <div className="stat-value">{stats.successful_jobs_24h}</div>
          <div className="stat-sub">of {stats.total_jobs_24h} total jobs</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-accent" style={{ background: 'var(--red)' }} />
          <div className="stat-label">Failed (24h)</div>
          <div className="stat-value" style={{ color: stats.failed_jobs_24h > 0 ? 'var(--red)' : 'var(--text)' }}>
            {stats.failed_jobs_24h}
          </div>
          <div className="stat-sub">jobs with errors</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-accent" style={{ background: 'var(--teal)' }} />
          <div className="stat-label">Rows Processed (24h)</div>
          <div className="stat-value">{stats.total_rows_24h.toLocaleString()}</div>
          <div className="stat-sub">
            {stats.next_scheduled
              ? `Next: ${formatDistanceToNow(new Date(stats.next_scheduled), { addSuffix: true })}`
              : 'Scheduler inactive'}
          </div>
        </div>
      </div>

      {/* Platform overview mode — Global Admin with no tenant selected */}
      {stats.platform_mode && (
        <div style={{ marginTop: 20 }}>
          <div style={{ padding: '20px 24px', background: 'rgba(37,99,235,0.06)', borderRadius: 10, border: '1px solid rgba(37,99,235,0.2)', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <Building2 size={18} style={{ color: 'var(--blue)' }} />
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Platform Overview</span>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-dim)', margin: '0 0 16px', lineHeight: 1.6 }}>
              You are viewing platform-wide statistics. Select a tenant from the switcher in the sidebar to view their dashboard, jobs, and connection settings.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {[
                { label: 'Active Tenants',    value: stats.total_tenants ?? 0,       color: 'var(--blue)' },
                { label: 'Total Jobs (24h)',  value: stats.total_jobs_24h ?? 0,      color: 'var(--text)' },
                { label: 'Successful (24h)',  value: stats.successful_jobs_24h ?? 0, color: 'var(--green)' },
                { label: 'Failed (24h)',      value: stats.failed_jobs_24h ?? 0,     color: (stats.failed_jobs_24h ?? 0) > 0 ? 'var(--red)' : 'var(--text)' },
              ].map(item => (
                <div key={item.label} style={{ padding: '16px 20px', background: 'var(--navy)', borderRadius: 8, border: '1px solid var(--navy-border)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{item.label}</div>
                  <div style={{ fontSize: 32, fontWeight: 700, color: item.color, lineHeight: 1 }}>{item.value.toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
          <Link to="/admin/tenants" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--blue)', textDecoration: 'none' }}>
            View all tenants →
          </Link>
        </div>
      )}

      {/* Tenant-specific cards — only shown when a tenant is selected */}
      {!stats.platform_mode && (
        <>
      {/* Bottom row — 3 cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isSFTP ? '1fr 1fr 1fr' : '1fr', gap: 16, marginTop: 16 }}>

        {/* Last Run Summary */}
        <div className="card" style={{ gridColumn: isSFTP ? '1' : '1' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div className="card-title" style={{ margin: 0 }}>Last Run Summary</div>
            {stats.last_job_id && (
              <Link to={`/jobs/${stats.last_job_id}`} style={{ fontSize: 12, color: 'var(--blue)' }}>
                View job →
              </Link>
            )}
          </div>

          {stats.last_run_files.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0', color: 'var(--text-dim)' }}>
              <FileText size={28} style={{ marginBottom: 8 }} />
              <span style={{ fontSize: 13 }}>
                {stats.last_status
                  ? stats.last_status === 'failed'
                    ? 'Last job failed — no files produced'
                    : 'No translated files recorded for last job'
                  : 'No jobs run yet'}
              </span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {stats.last_run_files.map(f => (
                <div key={f.file_name} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', background: 'var(--navy)',
                  borderRadius: 8, border: '1px solid var(--navy-border)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FileText size={13} style={{ color: 'var(--blue)', flexShrink: 0 }} />
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>
                      {f.file_name}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--text-dim)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Database size={11} /> {f.rows.toLocaleString()} rows
                    </span>
                    <span>{formatBytes(f.size_bytes)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SFTP folder status cards — only shown for SFTP tenants */}
        {isSFTP && (
          <>
            {/* Files waiting in incoming */}
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <FolderInput size={16} style={{ color: 'var(--amber)' }} />
                <div className="card-title" style={{ margin: 0 }}>Waiting to Translate</div>
              </div>
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <div style={{
                  fontSize: 48, fontWeight: 700, lineHeight: 1,
                  color: stats.incoming_count > 0 ? 'var(--amber)' : 'var(--text)',
                }}>
                  {stats.incoming_count}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 6 }}>
                  {stats.incoming_count === 0
                    ? 'incoming/ folder is empty'
                    : `file${stats.incoming_count !== 1 ? 's' : ''} in incoming/ folder`}
                </div>
              </div>
              {stats.incoming_count > 0 && (
                <button
                  onClick={handleRunNow}
                  disabled={running}
                  className="btn btn-primary btn-sm"
                  style={{ width: '100%', marginTop: 8 }}
                >
                  <Play size={13} /> Translate Now
                </button>
              )}
            </div>

            {/* Files waiting in outgoing */}
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <FolderOutput size={16} style={{ color: 'var(--teal)' }} />
                <div className="card-title" style={{ margin: 0 }}>Waiting for Collection</div>
              </div>
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <div style={{
                  fontSize: 48, fontWeight: 700, lineHeight: 1,
                  color: stats.outgoing_count > 0 ? 'var(--teal)' : 'var(--text)',
                }}>
                  {stats.outgoing_count}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 6 }}>
                  {stats.outgoing_count === 0
                    ? `${stats.collection_path || 'the collection folder'} is empty`
                    : `file${stats.outgoing_count !== 1 ? 's' : ''} in ${stats.collection_path || 'the collection folder'} ready for EzyCollect`}
                </div>
              </div>
              {stats.outgoing_count > 0 && (
                <div style={{
                  marginTop: 8, padding: '8px 12px', borderRadius: 8,
                  background: 'rgba(13,148,136,0.08)', border: '1px solid rgba(13,148,136,0.2)',
                  fontSize: 12, color: 'var(--teal)', textAlign: 'center',
                }}>
                  EzyCollect has files to collect
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* SFTP Connection Status — SFTP tenants only */}
      {isSFTP && stats.sftp_endpoint && (
        <div className="card" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Server size={15} style={{ color: 'var(--blue)' }} />
            <div className="card-title" style={{ margin: 0 }}>Hosted SFTP Connection</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Endpoint</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text)' }}>{stats.sftp_endpoint}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Status</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--green)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--green)', fontWeight: 500 }}>Active</span>
                </div>
              </div>
            </div>
            <Link to="/connection" style={{ fontSize: 12, color: 'var(--blue)' }}>
              Manage connection →
            </Link>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
